import {
  BaseSourceSiteWorkCentre,
  createMenuItems,
  PREFERRED_METHODS,
  PreferredMethod,
  Status,
  STATUS_MAP,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import {
  Box,
  CircularProgress,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ChangeEvent,
  MutableRefObject,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react'
import { GridColDef } from '@mui/x-data-grid'
import {
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { Button, DropDown } from 'Common/Supplyvue-ui'
import { ActionItem } from 'Common/Components/MUITableAction'
import { Table } from 'Common/MUITableV2'
import { useNavigate } from 'react-router-dom'
import RefreshIcon from '@mui/icons-material/Refresh'

export const PlanTemplateSummaryTable = ({
  data,
  handleSave,
  handleRunTemplates,
  isTemplateGenerating,
  handleRefresh,
  isRefetching,
}: {
  data: BaseSourceSiteWorkCentre[]
  handleSave: (
    unsavedChangesRef: MutableRefObject<{
      unsavedRows: Record<GridRowId, BaseSourceSiteWorkCentre>
      rowsBeforeChange: Record<GridRowId, BaseSourceSiteWorkCentre>
    }>
  ) => void
  handleRunTemplates: (selectedRows: GridRowId[]) => void
  isTemplateGenerating: boolean
  handleRefresh: () => void
  isRefetching: boolean
}) => {
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const navigate = useNavigate()
  const handleRowSelectionChange = (
    rowSelectionModel: GridRowSelectionModel
  ) => {
    setSelectedRows([...rowSelectionModel.ids])
  }

  const getTemplateGeneratedIcon = (status: Status) => {
    let content: ReactNode

    switch (status) {
      case 'completed':
        content = (
          <Tooltip title="Completed">
            <CheckCircleIcon color="primary" />
          </Tooltip>
        )
        break
      case 'unstarted':
        content = <RadioButtonUncheckedIcon color="disabled" />
        break
      case 'initiated':
        content = (
          <Tooltip title="Processing">
            <CircularProgress color="primary" size={20} />
          </Tooltip>
        )
        break
      case 'processing':
        content = (
          <Tooltip title="Processing">
            <CircularProgress color="primary" size={20} />
          </Tooltip>
        )
        break
      case 'failed':
        content = (
          <Tooltip title="Failed">
            <CancelOutlinedIcon color="error" />
          </Tooltip>
        )
        break
      default:
        content = (
          <Tooltip title="unstarted">
            <RadioButtonUncheckedIcon color="disabled" />
          </Tooltip>
        )
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        {content}
      </Box>
    )
  }

  const unsavedChangesRef = useRef<{
    unsavedRows: Record<GridRowId, BaseSourceSiteWorkCentre>
    rowsBeforeChange: Record<GridRowId, BaseSourceSiteWorkCentre>
  }>({
    unsavedRows: {},
    rowsBeforeChange: {},
  })

  const COLUMNS_WIDTH = 150

  const isComplete = (
    params: GridRowParams<BaseSourceSiteWorkCentre>
  ): boolean => {
    const {
      preferredTemplate,
      constrainedStatus,
      unconstrainedStatus,
      campaignStatus,
      carbonCostStatus,
    } = params.row

    const statusKey = STATUS_MAP[preferredTemplate] ?? 'constrainedStatus'

    const statusMap: Record<string, string | undefined> = {
      constrainedStatus,
      unconstrainedStatus,
      carbonCostStatus,
      campaignStatus,
    }

    return statusMap[statusKey] === 'completed'
  }

  const handleViewResults = (
    params: GridRowParams<BaseSourceSiteWorkCentre>
  ): void => {
    const { workCentreKey, preferredTemplate } = params.row

    if (isComplete(params)) {
      navigate(
        `template-results/${workCentreKey}/${preferredTemplate}/template`
      )
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMNS_WIDTH,
        flex: 2,
      },
      {
        field: 'workcentreCode',
        headerName: 'Workcentre Code',
        width: COLUMNS_WIDTH,
        flex: 2,
      },
      {
        field: 'workcentreCapacity',
        headerName: 'Workcentre Capacity',
        width: COLUMNS_WIDTH,
        type: 'number',
        flex: 1,
      },
      {
        field: 'headSpaceBuffer',
        headerName: 'Head Space Buffer',
        width: COLUMNS_WIDTH,
        type: 'number',
        flex: 1,
      },
      {
        field: 'preferredMethod',
        headerName: 'Preferred Method',
        width: 200,
        renderCell: (
          params: GridRenderCellParams<BaseSourceSiteWorkCentre>
        ) => {
          const { preferredTemplate, id } = params.row

          const handleDropdownChange = (
            e: SelectChangeEvent<string | number>
          ) => {
            const newPreferredTemplate = e.target.value as PreferredMethod

            const updatedRow = {
              ...params.row,
              preferredTemplate: newPreferredTemplate,
            }

            setHasUnsavedChanges(true)
            unsavedChangesRef.current.unsavedRows[id] = updatedRow
            if (!unsavedChangesRef.current.rowsBeforeChange[id]) {
              unsavedChangesRef.current.rowsBeforeChange[id] = updatedRow
            }

            const apiRef = params.api
            apiRef.updateRows([updatedRow])
          }

          return (
            <Box
              display="flex"
              onClick={(e) => e.stopPropagation()}
              sx={{ 
                height: '100%', 
                padding: '2px',
                alignItems: 'center',
              }}
            >
              <DropDown
                menuItems={createMenuItems(PREFERRED_METHODS)}
                label={'preferredMethod'}
                name={'preferredMethod'}
                fullWidth={true}
                variant={'outlined'}
                value={preferredTemplate ?? 'constrained'}
                onChange={(e) => handleDropdownChange(e)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: '32px',
                    height: '32px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 8px',
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Box>
          )
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 100,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams) => {
          const {
            preferredTemplate,
            constrainedStatus,
            unconstrainedStatus,
            carbonStatus,
            campaignStatus,
          } = params.row
          switch (preferredTemplate) {
            case 'constrained':
              return getTemplateGeneratedIcon(constrainedStatus)
            case 'unconstrained':
              return getTemplateGeneratedIcon(unconstrainedStatus)
            case 'carbonCost':
              return getTemplateGeneratedIcon(carbonStatus)
            case 'campaign':
              return getTemplateGeneratedIcon(campaignStatus)
            default:
              return getTemplateGeneratedIcon('unstarted')
          }
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        getActions: (params: GridRowParams<BaseSourceSiteWorkCentre>) => {
          return [
            <ActionItem
              label={'View Results'}
              clickHandler={() => {
                handleViewResults(params)
              }}
              showInMenu
              closeMenuOnClick={true}
              disabled={!isComplete(params)}
            />,
          ]
        },
      },
    ],
    [data]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Button
            variant="outlined"
            disabled={!hasUnsavedChanges}
            size="small"
            onClick={() => handleSave(unsavedChangesRef)}
          >
            Save
          </Button>
          <Button
            variant="text"
            sx={{ fontWeight: 500 }}
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Box mr={2}>
            <Typography color="primary">Select a Workcentre</Typography>
          </Box>
          <Button
            variant="outlined"
            disabled={selectedRows.length === 0}
            size="small"
            onClick={() => handleRunTemplates(selectedRows)}
          >
            {isTemplateGenerating && (
              <CircularProgress
                color="inherit"
                size={16}
                sx={{
                  marginRight: '8px',
                }}
              />
            )}
            Generate Plan Templates
          </Button>
        </Box>
      </Box>
      <Table
        rows={data}
        columns={columns}
        checkboxSelection={true}
        onRowSelectionModelChange={handleRowSelectionChange}
        showCellVerticalBorder
        showColumnVerticalBorder
        pagination
        getRowClassName={({ id }) => {
          return unsavedChangesRef.current.unsavedRows[id] ? 'row--edited' : ''
        }}
        loading={isRefetching}
        onRowClick={handleViewResults}
        sx={{
          '& .MuiDataGrid-row': {
            backgroundColor: 'transparent !important',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            },
          },
          '& .MuiDataGrid-row.row--odd': {
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            },
          },
          '& .MuiDataGrid-row.row--even': {
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            },
          },
          '& .MuiDataGrid-cell': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-main': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-root': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-row.row--odd .MuiDataGrid-cell': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-row.row--even .MuiDataGrid-cell': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-row.row--odd .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-row.row--even .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: 'transparent !important',
          },
          backgroundColor: 'transparent !important',
        }}
        rowHeight={40}
        hideFooter={true}
      />
    </Box>
  )
}
